import {
  ActionLabel,
  getPlaceholder,
  getPlaceholderForConversionReportType,
  getStatus,
  getStatusForConversionReportType,
  isFromScratch,
  isMinimalFiling,
  statusKeys
} from '../../../data';

import OutlinedNavigateIcon from '../../../../../../assets/icons/navigate-outlined.svg?react';
import { useCopyJurisdictions } from '../../../../../../services/automation.service';
import toast from 'react-hot-toast';
import { ClipLoader } from 'react-spinners';
import { FaArrowRight } from 'react-icons/fa6';
import { useNavigate, useParams } from 'react-router-dom';
import { REPORT_NAMINGS, REPORT_TYPES } from '../../../../../../consts/reportTypes';

// Navigate to the dedicated Jurisdictions page. Scratch periods land on the
// flat /:projectId namespace (matches /funds); automation/from-template
// periods nest under /quaters/:wf/:periodId.
const buildJurisdictionsPath = (workflowId, period) => {
  const isAutomation = period?.template_type && period.template_type !== 'sratch';
  const legacy = isAutomation
    ? `/project/quaters/${workflowId}/${period.id}/jurisdictions`
    : `/project/${workflowId}/jurisdictions?periodId=${period.id}`;

  return legacy;
};

export const JurisdictionColumn = ({
  period,
  refetch,
  reportType,
  setSelectedPeriodId,
  setIsOpenReadonlyJurisdictionDrawer
}) => {
  const navigate = useNavigate();
  const { id: workflowId } = useParams();
  const status =
    reportType === REPORT_TYPES.CONVERSION
      ? getStatusForConversionReportType(period)
      : getStatus(period, reportType);
  const placeholder =
    reportType === REPORT_TYPES.CONVERSION
      ? getPlaceholderForConversionReportType(period)
      : getPlaceholder(period, reportType);
  const copyJurisdictions = useCopyJurisdictions();
  const isFormPFReportType = reportType === REPORT_TYPES.FORM_PF;

  const handleCopy = () => {
    if (isFromScratch(period)) {
      setSelectedPeriodId(period.id);
      return navigate(buildJurisdictionsPath(workflowId, period));
    }

    copyJurisdictions.mutate(
      { period_id: period.id },
      {
        onSuccess: () => {
          refetch();
          toast.success(
            `${REPORT_NAMINGS.JURISDICTION?.[reportType]} from previous period have been copied`,
            {
              position: 'bottom-center'
            }
          );
        },
        onError: err => {
          setSelectedPeriodId(period.id);
          navigate(buildJurisdictionsPath(workflowId, period));
        }
      }
    );
  };

  const handleClick = () => {
    setSelectedPeriodId(period.id);
    // FormPF stores its funds outside `national_codes`, so the Annex IV-shaped
    // readonly drawer renders empty for FormPF periods (jurisdiction shows up,
    // but the "funds" expand panel is always blank). Skip the preview for
    // FormPF and jump straight to the editor page — that's how this column
    // behaved before the readonly-preview flow was introduced.
    if (isFormPFReportType) {
      navigate(buildJurisdictionsPath(workflowId, period));
      return;
    }
    // Otherwise preview in the readonly drawer first. The drawer's own
    // "Open" button (handleOpenJurisdictionDrawer in Quarters/index.jsx)
    // navigates to the dedicated Jurisdictions page when the user wants to
    // edit. The "no jurisdictions yet" path (handleCopy above) skips this —
    // it copies from the previous period (automation) or jumps straight to
    // the editor (scratch).
    setIsOpenReadonlyJurisdictionDrawer(true);
  };

  if (copyJurisdictions.isLoading) {
    return (
      <ActionLabel
        style={{ width: 150 }}
        label="Adding ..."
        rightIcon={<ClipLoader size={16} color="#228BE6" />}
      />
    );
  }

  if (isMinimalFiling(period)) {
    return <span style={{ color: '#adb5bd', fontSize: 14 }}>N/A</span>;
  }

  if (status === statusKeys.JURISDICTION && !period.jurisdiction_count)
    return (
      <ActionLabel
        style={{ width: 150 }}
        label={`Add ${REPORT_NAMINGS.JURISDICTION[reportType]}`}
        rightIcon={<OutlinedNavigateIcon />}
        onClick={handleCopy}
      />
    );

  if (!period.jurisdiction_count && period.jurisdiction_validation_progress_status !== 'Completed')
    return <ActionLabel style={{ width: 150 }} placeholder={placeholder} />;

  return (
    <ActionLabel
      style={{ width: 150 }}
      label={`${period.jurisdiction_count} ${REPORT_NAMINGS.JURISDICTION?.[reportType] || 'jurisdiction'}`}
      color={!period.jurisdiction_count ? '#228BE6' : '#40C057'}
      rightIcon={<FaArrowRight />}
      onClick={handleClick}
    />
  );
};
